import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  PaperPlaneTilt, ThumbsUp, ThumbsDown, CloudArrowUp, ArrowsClockwise,
} from '@phosphor-icons/react';

export const EVVCommonIssuesPanel = ({ issues }) => (
  <Card className="border-blue-200 bg-blue-50/30" data-testid="common-issues-panel">
    <CardHeader className="pb-3">
      <CardTitle className="text-base text-blue-900">Common Issues & Fixes</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {issues.map((issue, idx) => (
          <div key={issue.issue || idx} className="bg-white rounded-lg p-3 border border-blue-100">
            <p className="font-medium text-sm text-slate-900">{issue.issue}</p>
            <p className="text-xs text-slate-500 mt-1">{issue.description}</p>
            <p className="text-xs text-blue-700 mt-1 font-medium">Fix: {issue.fix}</p>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const EVVBulkActionBar = ({
  selectedIds, entries, canApprove, submitting,
  onRequestApproval, onOpenApproval, onSubmitSelected,
}) => {
  if (selectedIds.length === 0) return null;
  const selectedEntries = entries.filter((e) => selectedIds.includes(e.id));
  const hasApprovable = selectedEntries.some((e) => e.sandata_status === 'pending_approval');
  const hasRequestable = selectedEntries.some((e) =>
    ['draft', 'corrected', 'failed', 'rejected_approval'].includes(e.sandata_status || 'draft')
  );
  // Per product decision (Feb 13, 2026): supervisor approval is no longer a
  // gate for Sandata submission. Any entry that hasn't already been accepted
  // by Sandata can be (re)submitted. The "Request Approval" / "Approve" /
  // "Reject" buttons above remain available for agencies that still want to
  // use the approval workflow internally, but they no longer block submit.
  const hasSubmittable = selectedEntries.some(
    (e) => !['accepted', 'submitted'].includes(e.sandata_status || ''),
  );

  return (
    <Card className="border-indigo-200 bg-indigo-50/40" data-testid="bulk-action-bar">
      <CardContent className="pt-3 pb-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-indigo-900">{selectedIds.length} selected</span>
          <div className="h-5 w-px bg-indigo-200" />
          {hasRequestable && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => onRequestApproval(
                selectedIds.filter((id) => {
                  const e = entries.find((x) => x.id === id);
                  return ['draft', 'corrected', 'failed', 'rejected_approval'].includes(e?.sandata_status || 'draft');
                })
              )}
              data-testid="bulk-request-approval-btn"
            >
              <PaperPlaneTilt size={14} className="mr-1" /> Request Approval
            </Button>
          )}
          {canApprove && hasApprovable && (
            <>
              <Button
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => onOpenApproval('approve')}
                data-testid="bulk-approve-btn"
              >
                <ThumbsUp size={14} className="mr-1" /> Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                onClick={() => onOpenApproval('reject')}
                data-testid="bulk-reject-btn"
              >
                <ThumbsDown size={14} className="mr-1" /> Reject
              </Button>
            </>
          )}
          {hasSubmittable && (
            <Button
              size="sm"
              onClick={onSubmitSelected}
              disabled={submitting}
              className="bg-indigo-900 hover:bg-indigo-800"
              data-testid="submit-selected-btn"
            >
              {submitting
                ? <ArrowsClockwise size={14} className="mr-1 animate-spin" />
                : <CloudArrowUp size={14} className="mr-1" />}
              Submit to Sandata
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
